import React from 'react';

import { Add as AddIcon } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { FieldState, FormState } from 'final-form';
import { FormSpy } from 'react-final-form';

import { validateWithYupSchema } from '@hrlogics/react-form-utils/yup';

import { PeosListDocument, QueryMode, useSendInvitesToPeoByCvStaffMutation } from 'src/api';
import { Button, FieldArrayTemplate } from 'src/components/common';
import { AutocompleteField, CheckboxField, Field, Form, TextField } from 'src/components/finalForm';
import { useNotification } from 'src/hooks/app';
import { usePeoPayrollProvidersAutocomplete } from 'src/hooks/peoPayrollProvider';

import { INITIAL_VALUES, PEO_INITIAL_VALUES } from './constants';
import { InviteFormFields, InviteFormPeoFields, InviteFormValues } from './types';
import { convertFormValues, inviteFormValidationSchema } from './utils';

interface InvitePeoByCVStaffModalFormProps {
  closeModal: () => void;
  switchClosability: (isClosureAllowed: boolean) => void;
}

export const InvitePeoByCVStaffModalForm: React.FC<InvitePeoByCVStaffModalFormProps> = ({
  closeModal,
  switchClosability,
}) => {
  const { createApolloContext } = useNotification();

  const peoPpAutocompleteProps = usePeoPayrollProvidersAutocomplete({
    queryFilter: searchText => ({
      filter: { displayName: { contains: searchText, mode: QueryMode.Insensitive } },
    }),
  });

  const [sendInvites] = useSendInvitesToPeoByCvStaffMutation({
    context: createApolloContext({
      errorMessage: 'Failed to invite PEOs',
      successMessage: 'PEOs successfully invited',
    }),
    refetchQueries: [PeosListDocument],
  });

  const onSubmit = React.useCallback(
    async (formValues: InviteFormValues) => {
      switchClosability(false);
      try {
        await sendInvites({
          variables: convertFormValues(formValues),
        });

        switchClosability(true);
        closeModal();
      } catch (error) {
        console.error(error);
        switchClosability(true);
      }
    },
    [closeModal, sendInvites, switchClosability],
  );

  const handleFormChange = React.useCallback(
    (
      formValues: FormState<InviteFormValues>,
      getArrayFieldState: (fieldName: string) => FieldState<boolean> | undefined,
    ) => {
      const checkboxState = getArrayFieldState(InviteFormPeoFields.isRevenueShareEnabled);

      if (checkboxState?.touched && formValues.values.peoPayrollProvider) {
        checkboxState.change(false);
      }
    },
    [],
  );

  return (
    <Box>
      <Typography variant="body1" fontWeight="bold">
        Invite PEOs by using their email address
      </Typography>

      <Form<InviteFormValues>
        onSubmit={onSubmit}
        subscription={{ valid: true, submitting: true }}
        containerProps={{ mt: 4 }}
        initialValues={INITIAL_VALUES}
        validate={validateWithYupSchema(inviteFormValidationSchema)}
      >
        {({ submitting, valid }) => {
          return (
            <React.Fragment>
              <Box>
                <Field
                  name={InviteFormFields.peoPayrollProvider}
                  component={AutocompleteField}
                  label="Select PEO Payroll Provider (Optional)"
                  fullWidth
                  disabled={submitting}
                  {...peoPpAutocompleteProps}
                />
              </Box>

              <FieldArrayTemplate
                label="Invitation"
                name={InviteFormFields.peos}
                addIcon={<AddIcon />}
                onAdd={fields => fields.push(PEO_INITIAL_VALUES)}
                onRemove={(fields, index) => fields.remove(index)}
                containerProps={{ sx: { mt: 2 } }}
              >
                {({ renderFieldName, getArrayFieldState }) => (
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Field
                      name={renderFieldName(InviteFormPeoFields.email)}
                      label="Email Address"
                      placeholder="Type Email Address"
                      required
                      component={TextField}
                      fullWidth
                      disabled={submitting}
                    />
                    <FormSpy<InviteFormValues>
                      subscription={{ values: true }}
                      onChange={formValues => handleFormChange(formValues, getArrayFieldState)}
                    />
                    <FormSpy<InviteFormValues> subscription={{ values: true }}>
                      {({ values }) => (
                        <Field
                          name={renderFieldName(InviteFormPeoFields.isRevenueShareEnabled)}
                          label="Enable Revenue Share for this PEO"
                          component={CheckboxField}
                          fullWidth
                          disabled={submitting || !!values.peoPayrollProvider}
                        />
                      )}
                    </FormSpy>
                  </Box>
                )}
              </FieldArrayTemplate>

              <Box display="flex" gap={2} mt={4} justifyContent="center">
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => closeModal()}
                  disabled={submitting}
                >
                  Close
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  disabled={!valid || submitting}
                  loading={submitting}
                >
                  Send invite
                </Button>
              </Box>
            </React.Fragment>
          );
        }}
      </Form>
    </Box>
  );
};
